import React, { useState, useEffect, useMemo } from 'react';
import {
  Download, Loader2, Search, ChevronLeft, ChevronRight,
  Filter, FileText, Users, File, CheckCircle
} from 'lucide-react';
import axios from 'axios';
import './RecruiterSubmissionsDatabase.css';

/* ─── helpers ─── */
const displayVal = (val) =>
  val !== undefined && val !== null && val !== '' ? val : '--';

const getFreshFileUrl = (url) => {
  if (!url) return '#';
  return `${process.env.REACT_APP_API_BASE_URL}/api/candidates/view-file?url=${encodeURIComponent(url)}`;
};

const fmt = (dateStr) => {
  if (!dateStr) return '--';
  const d = new Date(dateStr);
  if (isNaN(d)) return '--';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getBadgeClass = (status = '') => {
  const s = status.toLowerCase();
  if (s.includes('select'))    return 'selected';
  if (s.includes('reject'))    return 'rejected';
  if (s.includes('interview')) return 'interview';
  return 'default';
};

/* ─── STANDARD columns ─── */
const STANDARD_COLS = [
  { key: 'date',       label: 'Submitted On',     sticky: false },
  { key: 'name',       label: 'Candidate Name',   sticky: true  },
  { key: 'role',       label: 'Job Role'           },
  { key: 'status',     label: 'Status'             },
  { key: 'email',      label: 'Email'              },
  { key: 'phone',      label: 'Phone'              },
  { key: 'totalExp',   label: 'Total Exp'          },
  { key: 'relExp',     label: 'Rel. Exp'           },
  { key: 'currentOrg', label: 'Current Org'        },
  { key: 'client',     label: 'Client'             },
  { key: 'currentCTC', label: 'Current CTC'        },
  { key: 'expCTC',     label: 'Expected CTC'       },
  { key: 'notice',     label: 'Notice Period'      },
  { key: 'working',    label: 'Working?'           },
  { key: 'lwd',        label: 'Last Working Day'   },
  { key: 'reason',     label: 'Reason for Change'  },
  { key: 'gapReason',  label: 'Gap Reason'         },
  { key: 'currLoc',    label: 'Current Location'   },
  { key: 'prefLoc',    label: 'Pref. Location'     },
  { key: 'education',  label: 'Education'          },
  { key: 'skills',     label: 'Skills'             },
  { key: 'resume',     label: 'Resume'             },
  { key: 'comments',   label: 'Comments'           },
];

/* ─── BEELINE columns — all 33 ─── */
const BEELINE_COLS = [
  { key: 'date',           label: 'Submitted On',    sticky: false },
  { key: 'name',           label: 'Candidate Name',  sticky: true  },
  { key: 'requestId',      label: 'Request ID'       },
  { key: 'beelineId',      label: 'Beeline ID'       },
  { key: 'email',          label: 'Email'            },
  { key: 'mobile',         label: 'Mobile'           },
  { key: 'dob',            label: 'DOB'              },
  { key: 'gender',         label: 'Gender'           },
  { key: 'nationality',    label: 'Nationality'      },
  { key: 'resourceType',   label: 'Resource Type'    },
  { key: 'initialId',      label: 'Initial ID'       },
  { key: 'fullPersonalId', label: 'Full Personal ID' },
  { key: 'linkedin',       label: 'LinkedIn'         },
  { key: 'totalExp',       label: 'Total Exp'        },
  { key: 'reqSkillExp',    label: 'Req Skill Exp'    },
  { key: 'degree',         label: 'Highest Degree'   },
  { key: 'specialization', label: 'Specialization'   },
  { key: 'recentCompany',  label: 'Recent Company'   },
  { key: 'companyAddress', label: 'Company Address'  },
  { key: 'eduStart',       label: 'Edu Start'        },
  { key: 'eduEnd',         label: 'Edu End'          },
  { key: 'candAddress',    label: 'Cand Address'     },
  { key: 'city',           label: 'City'             },
  { key: 'state',          label: 'State'            },
  { key: 'pincode',        label: 'Pincode'          },
  { key: 'subRate',        label: 'Sub. Rate'        },
  { key: 'payRate',        label: 'Pay Rate'         },
  { key: 'available',      label: 'Available?'       },
  { key: 'dateAvail',      label: 'Date Avail'       },
  { key: 'formerTCS',      label: 'Former TCS?'      },
  { key: 'formerBA',       label: 'Former BA?'       },
  { key: 'attachments',    label: 'Attachments'      },
];

/* ═══════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════ */
const RecruiterSubmissionsDatabase = ({ recruiterId }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [searchTerm, setSearchTerm]   = useState('');
  const [sortOption, setSortOption]   = useState('date-newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [isBeeline, setIsBeeline]     = useState(false);
  const ITEMS_PER_PAGE = 20;

  /* ── Fetch ── */
  useEffect(() => {
    const fetchData = async () => {
      if (!recruiterId) return;
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/candidates/recruiter/${recruiterId}`
        );
        setSubmissions(data);
      } catch (e) {
        console.error('Error fetching submissions:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [recruiterId]);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, sortOption, isBeeline]);

  /* ── Split by type ── */
  const standardSubs = useMemo(() =>
    submissions.filter(c => !c.isBeeline), [submissions]);
  const beelineSubs  = useMemo(() =>
    submissions.filter(c => c.isBeeline),  [submissions]);

  const active  = isBeeline ? beelineSubs : standardSubs;
  const COLUMNS = isBeeline ? BEELINE_COLS : STANDARD_COLS;

  /* ── Filter ── */
  const filtered = useMemo(() =>
    active.filter(c =>
      c.candidateName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.job?.role?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.job?.requestId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.job?.beelineId?.toLowerCase().includes(searchTerm.toLowerCase())
    ), [active, searchTerm]);

  /* ── Sort ── */
  const sorted = useMemo(() =>
    [...filtered].sort((a, b) =>
      sortOption === 'date-newest'
        ? new Date(b.submittedAt) - new Date(a.submittedAt)
        : new Date(a.submittedAt) - new Date(b.submittedAt)
    ), [filtered, sortOption]);

  /* ── Pagination ── */
  const totalPages   = Math.max(1, Math.ceil(sorted.length / ITEMS_PER_PAGE));
  const indexOfFirst = (currentPage - 1) * ITEMS_PER_PAGE;
  const indexOfLast  = Math.min(indexOfFirst + ITEMS_PER_PAGE, sorted.length);
  const currentItems = sorted.slice(indexOfFirst, indexOfLast);

  /* ── CSV Export ── */
  const handleExport = () => {
    if (sorted.length === 0) return alert('No data to export');

    let headers, rows;

    if (isBeeline) {
      headers = [
        'Submitted On','Candidate Name','Request ID','Beeline ID','Email','Mobile',
        'DOB','Gender','Nationality','Resource Type','Initial ID','Full Personal ID',
        'LinkedIn','Total Exp','Req Skill Exp','Highest Degree','Specialization',
        'Recent Company','Company Address','Edu Start','Edu End','Cand Address',
        'City','State','Pincode','Sub. Rate','Pay Rate','Available?','Date Avail',
        'Former TCS?','Former BA?',
      ];
      rows = sorted.map(c => [
        `"${fmt(c.submittedAt)}"`,
        `"${c.candidateName || ''}"`,
        `"${c.job?.requestId || c.requestId || ''}"`,
        `"${c.job?.beelineId || c.beelineId || ''}"`,
        `"${c.candidateEmail || ''}"`,
        `"${c.contactNumber || ''}"`,
        `"${fmt(c.dob)}"`,
        `"${c.gender || ''}"`,
        `"${c.nationality || ''}"`,
        `"${c.resourceType || ''}"`,
        `"${c.initialId || ''}"`,
        `"${c.fullPersonalId || ''}"`,
        `"${c.linkedin || ''}"`,
        `"${c.totalExperience || ''}"`,
        `"${c.experienceRequiredSkills || c.reqSkillExp || ''}"`,
        `"${c.highestDegree || ''}"`,
        `"${c.specialization || ''}"`,
        `"${c.recentCompany || c.currentCompanyName || ''}"`,
        `"${c.companyAddress || ''}"`,
        `"${fmt(c.eduStart)}"`,
        `"${fmt(c.eduEnd)}"`,
        `"${c.candAddress || c.currentLocation || ''}"`,
        `"${c.candidateCity || c.city || ''}"`,
        `"${c.state || ''}"`,
        `"${c.pincode || ''}"`,
        `"${c.submittedRate ? `$${c.submittedRate}` : c.subRate || ''}"`,
        `"${c.payRate || ''}"`,
        `"${c.available || ''}"`,
        `"${fmt(c.dateAvailable)}"`,
        `"${c.formerTCS || ''}"`,
        `"${c.formerBA || ''}"`,
      ].join(','));
    } else {
      headers = [
        'Submitted Date','Job Role','Candidate Name','Email','Phone','Status',
        'Total Exp','Rel Exp','Current Company','Client','Current CTC',
        'Expected CTC','Notice Period','Currently Working','Last Working Day',
        'Reason for Change','Gap Reason','Current Location','Pref Location',
        'Education','Skills','Comments',
      ];
      rows = sorted.map(c => [
        `"${fmt(c.submittedAt)}"`,
        `"${c.job?.role || 'N/A'}"`,
        `"${c.candidateName || ''}"`,
        `"${c.candidateEmail || ''}"`,
        `"${c.contactNumber || ''}"`,
        `"${c.candidateStatus || ''}"`,
        `"${c.totalExperience || ''}"`,
        `"${c.relevantExperience || ''}"`,
        `"${c.currentCompanyName || ''}"`,
        `"${c.clientName || ''}"`,
        `"${c.currentCTC || ''}"`,
        `"${c.expectedCTC || ''}"`,
        `"${c.noticePeriod || ''}"`,
        `"${c.currentlyWorking || ''}"`,
        `"${c.lastWorkingDay ? fmt(c.lastWorkingDay) : ''}"`,
        `"${c.reasonForChange || ''}"`,
        `"${c.reasonNotWorking || ''}"`,
        `"${c.currentLocation || ''}"`,
        `"${c.preferredLocation || ''}"`,
        `"${c.education || ''}"`,
        `"${c.skillSet || ''}"`,
        `"${c.comments || ''}"`,
      ].join(','));
    }

    const csv  = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `Recruiter_${isBeeline ? 'Beeline' : 'Standard'}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ══════════════════════════════
     CELL RENDERER — STANDARD
  ══════════════════════════════ */
  const renderStandardCell = (c, key) => {
    switch (key) {
      case 'date':
        return <td key={key} className="rsd-date-cell">{fmt(c.submittedAt)}</td>;
      case 'name':
        return <td key={key} className="rsd-td-sticky">{c.candidateName}</td>;
      case 'role':
        return <td key={key} className="rsd-role-cell">{displayVal(c.job?.role)}</td>;
      case 'status':
        return (
          <td key={key}>
            <span className={`rsd-badge ${getBadgeClass(c.candidateStatus)}`}>
              {c.candidateStatus || 'Pending'}
            </span>
          </td>
        );
      case 'email':      return <td key={key} className="rsd-email-cell">{displayVal(c.candidateEmail)}</td>;
      case 'phone':      return <td key={key}>{displayVal(c.contactNumber)}</td>;
      case 'totalExp':   return <td key={key}>{displayVal(c.totalExperience)}</td>;
      case 'relExp':     return <td key={key}>{displayVal(c.relevantExperience)}</td>;
      case 'currentOrg': return <td key={key}>{displayVal(c.currentCompanyName)}</td>;
      case 'client':     return <td key={key}>{displayVal(c.clientName)}</td>;
      case 'currentCTC': return <td key={key}>{displayVal(c.currentCTC)}</td>;
      case 'expCTC':     return <td key={key}>{displayVal(c.expectedCTC)}</td>;
      case 'notice':     return <td key={key}>{displayVal(c.noticePeriod)}</td>;
      case 'working':    return <td key={key}>{displayVal(c.currentlyWorking)}</td>;
      case 'lwd':        return <td key={key} className="rsd-date-cell">{fmt(c.lastWorkingDay)}</td>;
      case 'reason':     return <td key={key}>{displayVal(c.reasonForChange)}</td>;
      case 'gapReason':  return <td key={key}>{displayVal(c.reasonNotWorking)}</td>;
      case 'currLoc':    return <td key={key}>{displayVal(c.currentLocation)}</td>;
      case 'prefLoc':    return <td key={key}>{displayVal(c.preferredLocation)}</td>;
      case 'education':  return <td key={key}>{displayVal(c.education)}</td>;
      case 'skills':
        return (
          <td key={key} className="rsd-skills-cell" title={c.skillSet || ''}>
            {c.skillSet
              ? c.skillSet.length > 40 ? `${c.skillSet.substring(0, 40)}…` : c.skillSet
              : '--'}
          </td>
        );
      case 'resume':
        return (
          <td key={key}>
            {c.resumeFile?.fileUrl ? (
              <a
                href={getFreshFileUrl(c.resumeFile.fileUrl)}
                target="_blank" rel="noreferrer"
                className="rsd-resume-link"
              >
                <FileText size={11} /> View
              </a>
            ) : '--'}
          </td>
        );
      case 'comments': return <td key={key}>{displayVal(c.comments)}</td>;
      default:         return <td key={key}>--</td>;
    }
  };

  /* ══════════════════════════════
     CELL RENDERER — BEELINE (all 33 cols)
  ══════════════════════════════ */
  const renderBeelineCell = (c, key) => {
    switch (key) {
      case 'date':
        return <td key={key} className="rsd-date-cell">{fmt(c.submittedAt)}</td>;

      case 'name':
        return <td key={key} className="rsd-td-sticky">{c.candidateName}</td>;

      case 'requestId':
        return (
          <td key={key} className="rsd-mono-cell">
            {displayVal(c.job?.requestId || c.requestId)}
          </td>
        );

      case 'beelineId':
        return (
          <td key={key} className="rsd-mono-cell">
            {displayVal(c.job?.beelineId || c.beelineId)}
          </td>
        );

      case 'email':
        return <td key={key} className="rsd-email-cell">{displayVal(c.candidateEmail)}</td>;

      case 'mobile':
        return <td key={key}>{displayVal(c.contactNumber)}</td>;

      case 'dob':
        return <td key={key} className="rsd-date-cell">{fmt(c.dob)}</td>;

      case 'gender':
        return <td key={key}>{displayVal(c.gender)}</td>;

      case 'nationality':
        return <td key={key}>{displayVal(c.nationality)}</td>;

      case 'resourceType':
        return <td key={key}>{displayVal(c.resourceType)}</td>;

      case 'initialId':
        return (
          <td key={key} className="rsd-mono-cell">
            {displayVal(c.initialId)}
          </td>
        );

      case 'fullPersonalId':
        return (
          <td key={key} className="rsd-mono-cell">
            {displayVal(c.fullPersonalId)}
          </td>
        );

      case 'linkedin':
        return (
          <td key={key}>
            {c.linkedin ? (
              <a
                href={c.linkedin}
                target="_blank"
                rel="noreferrer"
                className="rsd-resume-link"
              >
                View
              </a>
            ) : '--'}
          </td>
        );

      case 'totalExp':
        return <td key={key}>{displayVal(c.totalExperience)}</td>;

      case 'reqSkillExp':
        return (
          <td key={key}>
            {displayVal(c.experienceRequiredSkills || c.reqSkillExp)}
          </td>
        );

      case 'degree':
        return <td key={key}>{displayVal(c.highestDegree)}</td>;

      case 'specialization':
        return <td key={key}>{displayVal(c.specialization)}</td>;

      case 'recentCompany':
        return (
          <td key={key}>
            {displayVal(c.recentCompany || c.currentCompanyName)}
          </td>
        );

      case 'companyAddress':
        return <td key={key}>{displayVal(c.companyAddress)}</td>;

      case 'eduStart':
        return (
          <td key={key} className="rsd-date-cell">
            {fmt(c.eduStart)}
          </td>
        );

      case 'eduEnd':
        return (
          <td key={key} className="rsd-date-cell">
            {fmt(c.eduEnd)}
          </td>
        );

      case 'candAddress':
        return (
          <td key={key}>
            {displayVal(c.candAddress || c.currentLocation)}
          </td>
        );

      case 'city':
        return (
          <td key={key}>
            {displayVal(c.candidateCity || c.city)}
          </td>
        );

      case 'state':
        return <td key={key}>{displayVal(c.state)}</td>;

      case 'pincode':
        return <td key={key}>{displayVal(c.pincode)}</td>;

      case 'subRate':
        return (
          <td key={key} className="rsd-rate-cell">
            {c.submittedRate
              ? `$${c.submittedRate}`
              : c.subRate
              ? `$${c.subRate}`
              : '--'}
          </td>
        );

      case 'payRate':
        return (
          <td key={key} className="rsd-rate-cell">
            {c.payRate ? `$${c.payRate}` : '--'}
          </td>
        );

      case 'available':
        return <td key={key}>{displayVal(c.available)}</td>;

      case 'dateAvail':
        return (
          <td key={key} className="rsd-date-cell">
            {fmt(c.dateAvailable)}
          </td>
        );

      case 'formerTCS':
        return <td key={key}>{displayVal(c.formerTCS)}</td>;

      case 'formerBA':
        return <td key={key}>{displayVal(c.formerBA)}</td>;

      /* ── Attachments: 3 file pills ── */
      case 'attachments':
        return (
          <td key={key}>
            <div className="rsd-file-group">
              {c.originalResumeUrl && (
                <a
                  href={getFreshFileUrl(c.originalResumeUrl)}
                  target="_blank"
                  rel="noreferrer"
                  className="rsd-file-link original"
                  title="Original Resume"
                >
                  <FileText size={10} /> Orig
                </a>
              )}
              {c.modifiedResumeUrl && (
                <a
                  href={getFreshFileUrl(c.modifiedResumeUrl)}
                  target="_blank"
                  rel="noreferrer"
                  className="rsd-file-link modified"
                  title="Pontoon Formatted Resume"
                >
                  <File size={10} /> Mod
                </a>
              )}
              {c.attestationPdfUrl && (
                <a
                  href={getFreshFileUrl(c.attestationPdfUrl)}
                  target="_blank"
                  rel="noreferrer"
                  className="rsd-file-link attest"
                  title="Pontoon Attestation"
                >
                  <CheckCircle size={10} /> Att
                </a>
              )}
              {!c.originalResumeUrl &&
               !c.modifiedResumeUrl &&
               !c.attestationPdfUrl && '--'}
            </div>
          </td>
        );

      default:
        return <td key={key}>--</td>;
    }
  };

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="rsd-loading">
        <Loader2 size={34} className="rsd-spin" />
        <p>Loading Recruiter Database…</p>
      </div>
    );
  }

  /* ════════════════════════════════
     RENDER
  ════════════════════════════════ */
  return (
    <div className="rsd-wrapper">

      {/* ══ TOOLBAR ══ */}
      <div className="rsd-toolbar">

        {/* Left: count */}
        <div className="rsd-toolbar-info">
          <span className="rsd-toolbar-count">{sorted.length}</span>
          <span className="rsd-toolbar-label">
            {isBeeline ? 'Beeline' : 'Standard'} submissions
          </span>
        </div>

        {/* Right: controls */}
        <div className="rsd-toolbar-controls">

          {/* Toggle */}
          <div
            className="rsd-toggle-group"
            onClick={() => setIsBeeline(b => !b)}
            title="Switch between Standard and Beeline"
          >
            <span className={`rsd-toggle-label ${!isBeeline ? 'active' : ''}`}>
              Standard
              {standardSubs.length > 0 && (
                <span className="rsd-toggle-count purple">
                  {standardSubs.length}
                </span>
              )}
            </span>

            <div className={`rsd-toggle-track ${isBeeline ? 'on' : 'off'}`}>
              <div className="rsd-toggle-thumb" />
            </div>

            <span className={`rsd-toggle-label ${isBeeline ? 'active beeline' : ''}`}>
              Beeline
              {beelineSubs.length > 0 && (
                <span className={`rsd-toggle-count ${isBeeline ? 'beeline' : ''}`}>
                  {beelineSubs.length}
                </span>
              )}
            </span>
          </div>

          {/* Divider */}
          <div className="rsd-toolbar-divider" />

          {/* Sort */}
          <div className="rsd-select-wrapper">
            <Filter size={13} className="rsd-select-icon" />
            <select
              className="rsd-select"
              value={sortOption}
              onChange={e => setSortOption(e.target.value)}
            >
              <option value="date-newest">Newest First</option>
              <option value="date-oldest">Oldest First</option>
            </select>
          </div>

          {/* Search */}
          <div className="rsd-search-wrapper">
            <Search size={13} className="rsd-search-icon" />
            <input
              className="rsd-search-input"
              type="text"
              placeholder={
                isBeeline
                  ? 'Search name, request ID…'
                  : 'Search candidate or role…'
              }
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Export */}
          <button className="rsd-btn-export" onClick={handleExport}>
            <Download size={13} /> Export CSV
          </button>
        </div>
      </div>

      {/* ══ TABLE ══ */}
      <div className="rsd-table-area">
        <table className={`rsd-table ${isBeeline ? 'beeline' : 'standard'}`}>
          <thead>
            <tr>
              {COLUMNS.map(col => (
                <th
                  key={col.key}
                  className={col.sticky ? 'rsd-th-sticky' : ''}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {currentItems.length === 0 ? (
              <tr className="rsd-empty-row">
                <td colSpan={COLUMNS.length}>
                  <div className="rsd-empty-state">
                    <div className="rsd-empty-icon">
                      <Users size={22} color="#a78bfa" />
                    </div>
                    <h4>
                      No {isBeeline ? 'Beeline' : 'Standard'} submissions found
                    </h4>
                    <p>
                      {searchTerm
                        ? 'Try adjusting your search.'
                        : `No ${isBeeline ? 'Beeline' : 'Standard'} candidates submitted yet.`}
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              currentItems.map(c => (
                <tr key={c._id}>
                  {COLUMNS.map(col =>
                    isBeeline
                      ? renderBeelineCell(c, col.key)
                      : renderStandardCell(c, col.key)
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ══ FOOTER ══ */}
      <div className="rsd-footer">
        <span className="rsd-showing-text">
          Showing{' '}
          <strong>
            {sorted.length === 0 ? 0 : indexOfFirst + 1}–{indexOfLast}
          </strong>
          {' '}of <strong>{sorted.length}</strong> results
        </span>

        <div className="rsd-pagination">
          <button
            className="rsd-page-btn"
            onClick={() => setCurrentPage(p => p - 1)}
            disabled={currentPage === 1}
          >
            <ChevronLeft size={14} />
          </button>
          <span className="rsd-page-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="rsd-page-btn"
            onClick={() => setCurrentPage(p => p + 1)}
            disabled={currentPage === totalPages}
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

    </div>
  );
};

export default RecruiterSubmissionsDatabase;